/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { CallProvider, CallData } from 'src/hooks/phones/types';
import { useTwilioCallProvider } from 'src/hooks/phones/providers/twilio/call.provider';
import { useBridgeCallProvider } from 'src/hooks/phones/providers/bridge/call.provider';
import { useBooleanSetting } from 'src/hooks/settings/settings';
import { SettingName } from 'src/api/generated';

/**
 * Represents a call event with callback data
 */
interface CallEvent {
  type: 'callStarted' | 'callEnded';
  data: CallData | undefined;
  defaultPrevented: boolean;
  preventDefault: () => void;
}

/**
 * Callbacks for handling call events
 */
interface CallEventCallbacks {
  onCallStarted?: (event: CallEvent) => void;
  onCallEnded?: (event: CallEvent) => void;
}

interface ProviderContext {
  provider: CallProvider | null;
  registerCallbacks: (callbacks: CallEventCallbacks) => () => void;
  isRoot: boolean;
  loading: boolean;
}

/**
 * Context for managing call provider state and callbacks throughout the application.
 * This context maintains a single provider instance and manages callback registration.
 */
const CallProviderContext = createContext<ProviderContext | null>(null);

/**
 * Props for the GlobalCallProvider component
 */
interface GlobalCallProviderProps {
  /** Child components that will have access to the call provider context */
  children: ReactNode;
  /** Optional callbacks for handling call events */
  callbacks?: CallEventCallbacks;
}

/**
 * Creates a new call event
 */
const createCallEvent = (
  type: 'callStarted' | 'callEnded',
  data: CallData | undefined
): CallEvent => {
  const event: CallEvent = {
    type,
    data,
    defaultPrevented: false,
    preventDefault: () => {
      event.defaultPrevented = true;
    },
  };
  return event;
};

/**
 * Provider component for managing call functionality throughout the application.
 * Implements a single provider instance pattern.
 */
export function GlobalCallProvider({
  children,
  callbacks,
}: GlobalCallProviderProps) {
  const context = useContext(CallProviderContext);
  const { setting: callWithoutInternet, loading: settingsLoading } =
    useBooleanSetting(SettingName.CALL_WITHOUT_INTERNET);

  const isRoot = !context;

  const callbackRegistry = useMemo(() => new Set<CallEventCallbacks>(), []);

  useEffect(() => {
    if (callbacks) {
      callbackRegistry.add(callbacks);
      return () => {
        callbackRegistry.delete(callbacks);
      };
    }
  }, [callbacks, callbackRegistry]);

  const registerCallbacks = useCallback(
    (callbacks: CallEventCallbacks) => {
      callbackRegistry.add(callbacks);
      return () => {
        callbackRegistry.delete(callbacks);
      };
    },
    [callbackRegistry]
  );

  const wrappedCallbacks = useMemo(
    () => ({
      onCallStarted: (data: CallData | undefined) => {
        const event = createCallEvent('callStarted', data);

        const callbacksInOrder = Array.from(callbackRegistry);
        callbacksInOrder.forEach((cb) => {
          if (!event.defaultPrevented) {
            cb.onCallStarted?.(event);
          }
        });
      },
      onCallEnded: (data: CallData | undefined) => {
        const event = createCallEvent('callEnded', data);

        const callbacksInOrder = Array.from(callbackRegistry);
        callbacksInOrder.forEach((cb) => {
          if (!event.defaultPrevented) {
            cb.onCallEnded?.(event);
          }
        });
      },
    }),
    [callbackRegistry]
  );

  const twilioProvider = useTwilioCallProvider(
    isRoot ? wrappedCallbacks : undefined
  );
  const bridgeProvider = useBridgeCallProvider(
    isRoot ? wrappedCallbacks : undefined
  );

  const isLoading =
    settingsLoading ||
    (twilioProvider && twilioProvider.loading) ||
    (bridgeProvider && bridgeProvider.loading);

  const provider = useMemo(() => {
    if (isLoading) return null;

    if (!isRoot && context) {
      return context.provider;
    }

    return callWithoutInternet ? bridgeProvider : twilioProvider;
  }, [
    isRoot,
    isLoading,
    callWithoutInternet,
    bridgeProvider,
    twilioProvider,
    context,
  ]);

  const value = useMemo(
    () => ({
      provider,
      registerCallbacks,
      isRoot,
      loading: isLoading,
    }),
    [provider, registerCallbacks, isRoot, isLoading]
  );

  return (
    <CallProviderContext.Provider value={value}>
      {children}
    </CallProviderContext.Provider>
  );
}

/**
 * Hook to register call event callbacks
 */
export function useCallCallbacks(callbacks: CallEventCallbacks) {
  const context = useContext(CallProviderContext);
  if (!context) {
    throw new Error(
      'useCallCallbacks must be used within a GlobalCallProvider'
    );
  }

  useEffect(() => {
    if (!callbacks) return;
    return context.registerCallbacks(callbacks);
  }, [context, callbacks]);
}

/**
 * Hook to access the current call provider context.
 */
export function useGlobalCallProviderContext(): {
  provider: CallProvider | null;
  loading: boolean;
} {
  const context = useContext(CallProviderContext);
  if (!context) {
    throw new Error(
      'useGlobalCallProviderContext must be used within a GlobalCallProvider'
    );
  }
  return { provider: context.provider, loading: context.loading };
}
